/**
 * "Introduction" documentation section (ADR-0074).
 *
 * Structure + `docs.*` keys only — prose lives in the per-locale catalogs. Source
 * material reconciled from `docs/quickstart.mdx`, `docs/concepts/*`, and the
 * locked product spec (`product/README.md`, `product/product-experience/*`,
 * `product/architecture/mcp-server.md`). Claims are kept accurate to shipped
 * behaviour and the published marketing surface (e.g. pricing/markup numbers are
 * not restated here — they link to `/pricing`).
 */
import { ARCHITECTURE_PAGE } from './pages/architecture.page';
import { FAQ_PAGE } from './pages/faq.page';
import { GLOSSARY_PAGE } from './pages/glossary.page';
import { LANGUAGES_PAGE } from './pages/languages.page';

import type { DocSection } from './types';

export const INTRODUCTION_SECTION: DocSection = {
  id: 'introduction',
  titleKey: 'docs.section.introduction.title',
  pages: [
    {
      slug: 'introduction',
      titleKey: 'docs.introduction.title',
      descriptionKey: 'docs.introduction.description',
      blocks: [
        { kind: 'paragraph', textKey: 'docs.introduction.lead' },

        {
          kind: 'heading',
          level: 2,
          id: 'what-it-is',
          textKey: 'docs.introduction.whatItIs.heading',
        },
        { kind: 'paragraph', textKey: 'docs.introduction.whatItIs.body' },
        {
          kind: 'list',
          itemKeys: [
            'docs.introduction.whatItIs.item.cli',
            'docs.introduction.whatItIs.item.widget',
            'docs.introduction.whatItIs.item.chat',
            'docs.introduction.whatItIs.item.mcp',
          ],
        },

        {
          kind: 'heading',
          level: 2,
          id: 'who-its-for',
          textKey: 'docs.introduction.whoItsFor.heading',
        },
        { kind: 'paragraph', textKey: 'docs.introduction.whoItsFor.body' },

        {
          kind: 'heading',
          level: 2,
          id: 'two-users',
          textKey: 'docs.introduction.twoUsers.heading',
        },
        { kind: 'paragraph', textKey: 'docs.introduction.twoUsers.body' },
        {
          kind: 'definitions',
          items: [
            {
              termKey: 'docs.introduction.twoUsers.builder.term',
              descKey: 'docs.introduction.twoUsers.builder.desc',
            },
            {
              termKey: 'docs.introduction.twoUsers.endUser.term',
              descKey: 'docs.introduction.twoUsers.endUser.desc',
            },
          ],
        },

        {
          kind: 'heading',
          level: 2,
          id: 'principles',
          textKey: 'docs.introduction.principles.heading',
        },
        {
          kind: 'list',
          itemKeys: [
            'docs.introduction.principles.item.control',
            'docs.introduction.principles.item.residency',
            'docs.introduction.principles.item.languages',
            'docs.introduction.principles.item.honest',
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          titleKey: 'docs.introduction.principles.note.title',
          textKey: 'docs.introduction.principles.note.body',
          links: {
            pricing: { target: { path: '/pricing' }, labelKey: 'docs.common.link.pricing' },
            trust: { target: { path: '/trust' }, labelKey: 'docs.common.link.trust' },
          },
        },

        { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
        {
          kind: 'cardGrid',
          cards: [
            {
              titleKey: 'docs.introduction.next.howItWorks.title',
              textKey: 'docs.introduction.next.howItWorks.text',
              to: { docSlug: 'how-it-works' },
            },
            {
              titleKey: 'docs.introduction.next.features.title',
              textKey: 'docs.introduction.next.features.text',
              to: { path: '/features' },
            },
            {
              titleKey: 'docs.introduction.next.pricing.title',
              textKey: 'docs.introduction.next.pricing.text',
              to: { path: '/pricing' },
            },
          ],
        },
      ],
    },

    {
      slug: 'how-it-works',
      titleKey: 'docs.howItWorks.title',
      descriptionKey: 'docs.howItWorks.description',
      blocks: [
        { kind: 'paragraph', textKey: 'docs.howItWorks.lead' },
        {
          kind: 'callout',
          tone: 'important',
          titleKey: 'docs.howItWorks.catalog.title',
          textKey: 'docs.howItWorks.catalog.body',
        },

        { kind: 'heading', level: 2, id: 'embed', textKey: 'docs.howItWorks.embed.heading' },
        { kind: 'paragraph', textKey: 'docs.howItWorks.embed.body' },
        {
          kind: 'code',
          language: 'html',
          code: '<script\n  type="module"\n  src="https://cdn.syncanix.com/widget.js"\n  data-key="pk_live_..."\n></script>',
        },

        { kind: 'heading', level: 2, id: 'discover', textKey: 'docs.howItWorks.discover.heading' },
        { kind: 'paragraph', textKey: 'docs.howItWorks.discover.body' },
        { kind: 'paragraph', textKey: 'docs.howItWorks.discover.note' },

        { kind: 'heading', level: 2, id: 'chat', textKey: 'docs.howItWorks.chat.heading' },
        { kind: 'paragraph', textKey: 'docs.howItWorks.chat.body' },
        {
          kind: 'list',
          itemKeys: [
            'docs.howItWorks.chat.tier.read',
            'docs.howItWorks.chat.tier.write',
            'docs.howItWorks.chat.tier.destructive',
          ],
        },

        { kind: 'heading', level: 2, id: 'mcp', textKey: 'docs.howItWorks.mcp.heading' },
        { kind: 'paragraph', textKey: 'docs.howItWorks.mcp.body' },

        { kind: 'heading', level: 2, id: 'control', textKey: 'docs.howItWorks.control.heading' },
        { kind: 'paragraph', textKey: 'docs.howItWorks.control.body' },

        { kind: 'heading', level: 2, id: 'journey', textKey: 'docs.howItWorks.journey.heading' },
        { kind: 'paragraph', textKey: 'docs.howItWorks.journey.body' },
        {
          kind: 'steps',
          steps: [
            {
              titleKey: 'docs.howItWorks.journey.step.embed.title',
              textKey: 'docs.howItWorks.journey.step.embed.text',
            },
            {
              titleKey: 'docs.howItWorks.journey.step.discover.title',
              textKey: 'docs.howItWorks.journey.step.discover.text',
            },
            {
              titleKey: 'docs.howItWorks.journey.step.review.title',
              textKey: 'docs.howItWorks.journey.step.review.text',
            },
            {
              titleKey: 'docs.howItWorks.journey.step.promote.title',
              textKey: 'docs.howItWorks.journey.step.promote.text',
            },
          ],
        },

        { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
        {
          kind: 'cardGrid',
          cards: [
            {
              titleKey: 'docs.howItWorks.next.intro.title',
              textKey: 'docs.howItWorks.next.intro.text',
              to: { docSlug: 'introduction' },
            },
            {
              titleKey: 'docs.howItWorks.next.features.title',
              textKey: 'docs.howItWorks.next.features.text',
              to: { path: '/features' },
            },
          ],
        },
      ],
    },
    ARCHITECTURE_PAGE,
    LANGUAGES_PAGE,
    GLOSSARY_PAGE,
    FAQ_PAGE,
  ],
};
